import type {
  CustomFontOptions,
  SupportBaseKey,
  SentryTeamName,
} from '@stripe-internal/connect-embedded-lib';
import type {
  AppearanceOptions,
  ThemePresetsKeys,
} from '@stripe-internal/embedded-theming';
import type {Flags} from '@sail/access';
import type {Feature} from '../../data-layer-client/FeaturesContext';
import type {IAccountSessionComponents} from '../../data-layer-client/AuthContext/AuthContext';
import type {IConnectElementSupplementalObject} from '../ConnectElementSupplementalObjects';
import type {ConnectHtmlAttributeType} from './HtmlAttributes';
import type {ConnectElementImportKeysComponentOnly} from './ConnectElementList';
import type {RiskSignalsCollectionHandler} from './RiskSignalCollectionTypes';
import type {MobileSdkUserAgentType} from '../../utils/mobile-sdk/getMobileSdkVersion';

/**
 * Represents the app that is hosting connect embedded components. If it is not specified, then it means we are embedding in a platform.
 * This value should be used for analytics purposes only. Do not use it to change the behavior of the components.
 */
export type USE_FOR_ANALYTICS_ONLY_HostApp =
  | 'express'
  | 'standard'
  | 'direct_settings'
  | 'platform'
  | 'platform_android'
  | 'platform_ios'
  | 'platform_react_native'
  | 'stripe_dashboard_android'
  | 'stripe_dashboard_ios'
  | 'web_test'
  | 'loginAs'
  | 'docs'
  | 'hostedOnboarding'
  | 'hostedOnboarding_custom'
  | 'hostedOnboarding_standard'
  | 'hostedOnboarding_standardOAuth'
  | 'hostedOnboarding_express'
  | 'hostedOnboarding_durable'
  | 'hostedOnboarding_recipientPayouts'
  | 'durableFlow_embeddedTaxDocuments'
  | 'durableFlow_capital';

export const defaultHostApp = 'platform';

export type sdkOptions = {
  sdkVersion?: string;
  reactSdkVersion?: string;
};

export type IFeatureOverrides = Partial<
  Record<keyof IAccountSessionComponents, Partial<Record<Feature, boolean>>>
>;

export enum UserAgentOverride {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export type MetaOptions = {
  analyticsRbEventId?: string;
  apiKeyOverride?: string;
  stripeDashboardSessionUserToken?: string;
  workspaceOverrideForRetrieveEmbeddedFlags?: string;
  disableAnalyticsInStripeDomain?: boolean;
  eagerClientSecretPromise?: Promise<string>;
  featureOverrides?: IFeatureOverrides;
  flagOverrides?: Partial<Flags>;
  /**
   * The hostApp specified by the UI surface embedding ConnectJS. This value is used for analytics purposes only.
   * Do not use this value to change the behavior of the components.
   * https://trailhead.corp.stripe.com/docs/connect-integration-guide/connect-embedded-components/embedded-components-in-stripe-owned-surfaces#hostapp-should-only-be-used-for-analytics-purposes
   */
  hostApp?: USE_FOR_ANALYTICS_ONLY_HostApp;
  livemodeOverride?: boolean;
  merchantIdOverride?: string;
  /**
   * Used specifically to pass in the customer session id, which is used in some requests when
   * calling the API from the customer portal
   */
  customerSessionId?: string;
  mobileSdk?: boolean;
  mobileSdkVersion?: string;

  /**
   * The user agent type of the Mobile SDK in use. Used to distinguish the SDK
   * of the Stripe Dashboard app built with React Native from the legacy native iOS app.
   * @example 'ios', 'android', 'react_native'
   */
  mobileSdkUserAgentType?: MobileSdkUserAgentType;
  mobileSdkBundleAppId?: string;
  mobileSdkDurableFlow?: boolean;
  userAgentOverride?: UserAgentOverride;
  releaseCandidate?: string;
  restrictToPlatformOwnsOnboarding?: boolean;
  platformIdOverride?: string;
  sdk?: boolean;
  sdkOptions?: sdkOptions;
  supportSiteBaseOverride?: Exclude<SupportBaseKey, 'embedded'>;
  granularApiKeyOverrides?: {
    v2?: {
      hostOverride?: string;
      apiKeyOverride?: string;
    };
  };
  themingPreset?: ThemePresetsKeys;
  mobileAuthedWebviewEntryPointInvokingComponent?: ConnectElementImportKeysComponentOnly;
  mobileAuthedWebviewEntryPointObservabilityProject?: SentryTeamName;
  mobileAuthedWebviewHtmlAttributes?: Partial<
    Record<ConnectHtmlAttributeType, string>
  >;
  mobileAuthedWebviewSupplementalObjects?: IConnectElementSupplementalObject;
  enableMcpTokenExchangeMechanism?: boolean;
  enableV2RootTokenFetch?: boolean;
  serviceEnvironmentOverride?: string;
};

export type DisplayOptions = {
  showObjectIds?: boolean;
};

export type InitOptions = {
  appearance?: AppearanceOptions;
  clientSecret?: string;
  fetchClientSecret?: () => Promise<string>;
  locale?: string;
  metaOptions?: MetaOptions;
  publishableKey: string;
  refreshClientSecret?: () => Promise<string>;
  fonts?: CustomFontOptions;
  onRiskSignalsCollection?: RiskSignalsCollectionHandler;
  displayOptions?: DisplayOptions;
};

export type UpdateOptions = {
  appearance?: AppearanceOptions;
  sdkOptions?: sdkOptions;
  locale?: string;
  metaOptions?: MetaOptions;
  displayOptions?: DisplayOptions;
};
